import { useEffect, useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { fetcher } from "../../../../services/fetcher";
import { 
  Select, SimpleGrid, Group, Button, Textarea, 
  FileInput, TextInput 
} from "@mantine/core";
import { OperatorProfile_API } from "../../../../api/endpoints";
import {
  BookingPriority,
  PaymentStatus,
  BookingReceived
} from "../../utils/bookingOptions";

const BookingForm = ({ 
  onSubmit, 
  onFormChange = () => {}, 
  initialData = {}, 
  selectedCover, 
  hideFields = [],
  bookingType,
  selectedOperator: externalSelectedOperator,
}) => {
  const userProfile = useSelector((state) => state.userProfile?.user);
  const { data: operatorListData } = useSWR(OperatorProfile_API, fetcher);
  const salesAgentId = userProfile?.id || "";

  const operatorSelectOptions = useMemo(() => {
    const operators = Array.isArray(operatorListData)
      ? operatorListData
      : operatorListData?.results ?? [];
    const seen = new Set();

    const options = operators.reduce((acc, op) => {
      const name = String(op?.operator_name || "Unnamed Operator");
      if (seen.has(name)) return acc;
      seen.add(name);
      acc.push({ value: name, label: name });
      return acc;
    }, []);

    return [...options, { value: "others", label: "Others" }];
  }, [operatorListData]);

  const {
    handleSubmit,
    control,
    register,
    setValue,
    watch, 
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...initialData,
      sales_agent: salesAgentId,
      plan_name: selectedCover?.label || "",
      client_or_operator: initialData?.client_or_operator?.toString() || "",
      purchase_method: initialData?.purchase_method || "SALES_TEAM",
      booking_type: initialData?.booking_type || bookingType || ""
    },
  });

  const formData = watch(); 
  const selectedOperatorWatch = useWatch({ control, name: "client_or_operator" });
  const selectedOperatorValue = externalSelectedOperator ?? selectedOperatorWatch;

  useEffect(() => {
    if (externalSelectedOperator != null) {
      setValue("client_or_operator", externalSelectedOperator);
    }
  }, [externalSelectedOperator, setValue]);

  // Effect to update plan_name when selectedCover changes
  useEffect(() => {
    if (selectedCover) {
      setValue("plan_name", selectedCover.label);
    }
  }, [selectedCover, setValue]);

  // Effect to update sales_agent when userProfile updates
  useEffect(() => {
    if (salesAgentId) {
      setValue("sales_agent", salesAgentId);
    }
  }, [salesAgentId, setValue]);

  useEffect(() => {
    if (bookingType) {
      setValue("booking_type", bookingType);
    }
  }, [bookingType, setValue]);

  // Effect to notify parent component about form changes
  useEffect(() => {
    onFormChange(formData);
  }, [formData, onFormChange]);

  useEffect(() => {
    if (selectedOperatorValue !== "others") {
      setValue("custom_operator_name", "");
    }
  }, [selectedOperatorValue, setValue]);
  
  // useEffect(() => {
  //     if (formData) {
  //       // scrollToElementWithOffset("#form", 50);
  //     }
  //   }, [formData]);
  
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <SimpleGrid cols={2} id="form" mt="lg">
          <Controller
            name="sales_agent"
            control={control}
            rules={{ required: "Sales agent is required" }}
            render={() => (
              <TextInput
                label="Sales Agent"
                withAsterisk
                placeholder="Enter Sales Agent Name"
                value={`${userProfile?.first_name || ""} ${userProfile?.last_name || ""}`.trim()}
                readOnly
                error={errors.sales_agent?.message}
              />
            )}
          />
          {!hideFields.includes("plan_name") && (
            <Controller
              name="plan_name"
              control={control}
              rules={{ required: "Plan name is required" }}
              render={({ field }) => (
                <TextInput 
                  label="Plan Name" 
                  withAsterisk
                  placeholder="Enter Plan Name" 
                  {...field} 
                  value={field.value || selectedCover?.label || ""}
                  error={errors.plan_name?.message} 
                />
              )}
            />
          )} 
          {!hideFields.includes("client_or_operator") ? (
            <Controller
              name="client_or_operator"
              control={control}
              rules={{ required: "Client/Operator is required" }}
              render={({ field }) => (
                <Select
                  label="Operator Name"
                  withAsterisk
                  placeholder="Select an operator"
                  searchable
                  data={operatorSelectOptions}
                  value={field.value?.toString() || ""}
                  onChange={(val) => field.onChange(val)}
                  error={errors.client_or_operator?.message}
                />
              )}
            />
          ) : (
            <input
              type="hidden"
              value={externalSelectedOperator || ""}
              {...register("client_or_operator")}
            />
          )}
          {selectedOperatorValue === "others" && (
            <Controller
              name="custom_operator_name"
              control={control}
              rules={{ required: "Please enter operator name" }}
              render={({ field }) => (
                <TextInput
                  label="Operator Name"
                  withAsterisk
                  placeholder="Enter operator name"
                  error={errors.custom_operator_name?.message}
                  {...field}
                />
              )}
            />
          )}
          {!hideFields.includes("purchase_method") && (
            <TextInput
              label="Purchase Method"
              withAsterisk
              placeholder="Purchase method"
              error={errors.purchase_method?.message}
              {...register("purchase_method", { required: "Purchase Method is required" })}
            />
          )}  
          <Controller
            name="booking_type"
            control={control}
            rules={{ required: "Booking Type is required" }}
            render={({ field }) => (
              <TextInput
                label="Booking Type"
                withAsterisk
                value={field.value || bookingType || ""}
                readOnly
                error={errors.booking_type?.message}
              />
            )}
          />
          <Controller
            name="priority"
            control={control}
            rules={{ required: "Priority is required" }}
            render={({ field }) => (
              <Select 
                label="Booking Priority" 
                withAsterisk 
                data={BookingPriority} 
                placeholder="Booking Priority" 
                {...field} 
                error={errors.priority?.message} 
              />
            )}
          />
          {!hideFields.includes("payment_status") && (
            <Controller
              name="payment_status"
              control={control}
              rules={{ required: "Payment status is required" }}
              render={({ field }) => (
                <Select 
                  label="Payment Status" 
                  withAsterisk 
                  data={PaymentStatus} 
                  placeholder="Payment Status" 
                  {...field} 
                  error={errors.payment_status?.message} 
                />
              )}
            />
          )}
          <Controller
            name="booking_received"
            control={control}
            rules={{ required: "Booking received is required" }}
            render={({ field }) => (
              <Select 
                label="Lead Source" 
                withAsterisk 
                data={BookingReceived} 
                placeholder="Booking Received" 
                {...field} 
                error={errors.booking_received?.message} 
              />
            )}
          />
          {!hideFields.includes("csv_file") && (
            <Controller
              name="csv_file"
              control={control}
              rules={{ required: "CSV file is required" }}
              render={({ field }) => (
                <FileInput
                  name="csv_file"
                  label="CSV File"
                  withAsterisk 
                  placeholder="Upload CSV"
                  clearable
                  accept=".csv"
                  onChange={(file) => {
                    field.onChange(file);
                  }}
                  error={errors.csv_file?.message} 
                />
              )}
            />
          )}
          {!hideFields.includes("payment_receipt") && (
            <Controller
              name="payment_receipt"
              control={control}
              render={({ field }) => (
                <FileInput 
                  name="payment_receipt"
                  label="Payment Receipt" 
                  placeholder="Upload Receipt" 
                  clearable 
                  accept="image/png,image/jpeg,application/pdf"
                  onChange={(file) => field.onChange(file)}
                  error={errors.payment_receipt?.message} 
                />
              )}
            />
          )}
          <Textarea label="Remark" placeholder="Enter remarks" {...register("remarks")} />
        </SimpleGrid>
        {!hideFields.includes("submit") && (
          <Group justify="center">
            <Button variant="gradient" type="submit" mt="xl" size="md">
              Submit
            </Button>
          </Group>
        )}
      </form>
    </>
  );
};

export default BookingForm;